import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  createAbuseReport,
  createComment,
  deleteComment,
  deletePost,
  fetchTrainingDisciplines,
  fetchWeightClasses,
  getAllPosts,
  getCommentsByPost,
  getLikesByPost,
  toggleLike,
  updatePost,
  uploadMultipleFile,
} from "../../services/api";
import {
  CommentRequestDto,
  CommentResponseDto,
  CommonProfileDto,
  CreateAbuseReportRequest,
  EditingPost,
  FileMetadata,
  PostResponseDto,
} from "../../types/types";
import { FeedsContent, FeedsFilter } from "./feedsContent";


const Feeds: React.FC = () => {
  const [posts, setPosts] = useState<PostResponseDto[]>([]);
  const [deleteModalPostId, setDeleteModalPostId] = useState<number | null>(null);
  const [reportModalPostId, setReportModalPostId] = useState<number | null>(null);
  const [reportReason, setReportReason] = useState<string>("");
  const [editingPost, setEditingPost] = useState<EditingPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"all" | "my">("all");
  const [comments, setComments] = useState<{ [postId: number]: CommentResponseDto[] }>({});
  const [newComment, setNewComment] = useState<{ [postId: number]: string }>({});
  const [likedPosts, setLikedPosts] = useState<{ [postId: number]: boolean }>({});
  const [showOptionsMenu, setShowOptionsMenu] = useState<number | null>(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState<{ [postId: number]: number }>({});
  const [expandedComments, setExpandedComments] = useState<{ [postId: number]: boolean }>({});
  const [showLikesModal, setShowLikesModal] = useState<number | null>(null);
  const [likesData, setLikesData] = useState<any[]>([]);
  const [loadingLikes, setLoadingLikes] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedSport, setSelectedSport] = useState<string>("");
  const [selectedWeight, setSelectedWeight] = useState<string>("");
  const [selectedManagerType, setSelectedManagerType] = useState<string>("");
  const [selectedProfileType, setSelectedProfileType] = useState<string>("");
  const { t } = useTranslation("feeds");
  const [sports, setSports] = useState<string[]>([]);
  const [weightClasses, setWeightClasses] = useState<string[]>([]);
  const [profiles, setProfiles] = useState<CommonProfileDto[]>([]);
  const [topProfiles, setTopProfiles] = useState<CommonProfileDto[]>([]);



  const loginResponse = JSON.parse(localStorage.getItem("loginResponse") || "{}");
  const currentUserId = loginResponse.id;
  const currentUsername = loginResponse.username || "";

const fetchPosts = useCallback(
  async (isMyPost: boolean) => {
    setLoading(true);
    try {
      const filters = {
        title: searchQuery.trim() || undefined,
        trainingDiscipline: selectedSport || undefined,
        weightClass: selectedWeight || undefined,
        managerType: selectedManagerType || undefined,
        profileType: selectedProfileType || undefined,
      };
      const data = await getAllPosts(currentUsername, isMyPost, filters);

      setPosts(data.posts || []);
setTopProfiles(data.profiles || []);
      setLikedPosts(
        (data.posts || []).reduce(
          (acc, post) => ({ ...acc, [post.id]: post.liked }),
          {} as { [postId: number]: boolean }
        )
      );
      setCurrentMediaIndex(
        (data.posts || []).reduce(
          (acc, post) => ({ ...acc, [post.id]: 0 }),
          {} as { [postId: number]: number }
        )
      );
    } catch (error) {
      toast.error(t("failedFetchPosts", "Failed to load posts"));
    } finally {
      setLoading(false);
    }
  },
  [
    searchQuery,
    selectedSport,
    selectedWeight,
    selectedManagerType,
    selectedProfileType,
    currentUsername,
    t,
  ]
);



  const loadSportsIfNeeded = useCallback(async () => {
    if (sports.length === 0) {
      try {
        setSports(await fetchTrainingDisciplines());
      } catch (e) {
        toast.error(t("failed_to_load_training_disciplines"));
      }
    }
  }, [sports.length, t]);

  const loadWeightClassesIfNeeded = useCallback(async () => {
    if (weightClasses.length === 0) {
      try {
        setWeightClasses(await fetchWeightClasses());
      } catch (e) {
        toast.error(t("failed_to_load_weight_classes"));
      }
    }
  }, [weightClasses.length, t]);

  useEffect(() => {
    fetchPosts(activeTab === "my");
  }, [activeTab, fetchPosts]);

  const handleTabChange = (key: React.Key) => {
    setActiveTab(key as "all" | "my");
  };

  const resetFilters = useCallback(() => {
    setSearchQuery("");
    setSelectedSport("");
    setSelectedWeight("");
    setSelectedManagerType("");
    setSelectedProfileType("");
  }, []);

  const handleLike = useCallback(
    async (postId: number, isLiked: boolean) => {
      try {
        await toggleLike(currentUsername, postId, !isLiked);
        setLikedPosts((prev) => ({ ...prev, [postId]: !isLiked }));
        setPosts((prev) =>
          prev.map((post) =>
            post.id === postId
              ? { ...post, totalLikes: isLiked ? post.totalLikes - 1 : post.totalLikes + 1 }
              : post
          )
        );
      } catch (error) {
        toast.error(t("failedToggleLike", "Failed to toggle like"));
      }
    },
    [currentUsername, t]
  );

  const handleShowLikes = useCallback(
    async (postId: number) => {
      setShowLikesModal(postId);
      setLoadingLikes(true);
      try {
        const likes = await getLikesByPost(postId);
        setLikesData(likes);
      } catch (error) {
        toast.error(t("failedFetchLikes", "Failed to load likes"));
      } finally {
        setLoadingLikes(false);
      }
    },
    [t]
  );

  const handleCloseLikesModal = useCallback(() => {
    setShowLikesModal(null);
    setLikesData([]);
  }, []);

  const handleDelete = useCallback(
    async (postId: number) => {
      try {
        await deletePost(currentUsername, postId);
        setPosts((prev) => prev.filter((post) => post.id !== postId));
        setLikedPosts((prev) => {
          const newLikedPosts = { ...prev };
          delete newLikedPosts[postId];
          return newLikedPosts;
        });
        setDeleteModalPostId(null);
        setShowOptionsMenu(null);
        toast.success(t("postDeletedSuccess"));
      } catch (error: any) {
        const errMsg = error?.response?.data?.message || error.message;
        toast.error(errMsg);
      }
    },
    [currentUsername, t]
  );

  const handleEdit = useCallback((post: PostResponseDto) => {
    setEditingPost({
      postId: post.id,
      title: post.title,
      content: post.content || "",
      newFiles: [],
      clearMedia: false,
    });
    setShowOptionsMenu(null);
  }, []);

  const removeSingleMedia = useCallback((index: number) => {
    setEditingPost((prev) => {
      if (!prev) return prev;

      if (!prev.clearMedia && index === 0) {
        setPosts((prevPosts) =>
          prevPosts.map((p) =>
            p.id === prev.postId ? { ...p, file: undefined } : p
          )
        );
        return { ...prev, clearMedia: true };
      } else {
        const newFileIndex = index - (prev.clearMedia ? 0 : 1);
        const updatedNewFiles = [...prev.newFiles];
        updatedNewFiles.splice(newFileIndex, 1);
        return { ...prev, newFiles: updatedNewFiles };
      }
    });

    setCurrentMediaIndex((prev) => {
      const postId = Object.keys(prev)[0] as unknown as number;
      return { ...prev, [postId]: 0 };
    });
  }, []);

  const handleSavePost = useCallback(
    async (postId: number) => {
      if (!editingPost) return;

      if (!editingPost.title.trim()) {
        toast.error(t("titleRequired"));
        return;
      }
      if (editingPost.title.length < 2 || editingPost.title.length > 100) {
        toast.error(t("titleLengthError"));
        return;
      }
      if (!editingPost.content.trim()) {
        toast.error(t("descriptionRequired"));
        return;
      }
      if (editingPost.content.length > 2000) {
        toast.error(t("descriptionLengthError"));
        return;
      }
      if (editingPost.newFiles.length > 10) {
        toast.error(t("maxFilesError"));
        return;
      }

      try {
        const post = posts.find((p) => p.id === postId);
        if (!post) throw new Error(t("postNotFound", "Post not found"));

        let uploadedFilesMetadata: FileMetadata[] = [];
        if (editingPost.newFiles.length > 0) {
          uploadedFilesMetadata = await uploadMultipleFile(editingPost.newFiles);
        }

        const newFileMetadata = uploadedFilesMetadata.length > 0  ? uploadedFilesMetadata[0]  : editingPost.clearMedia ? null :(post.file ?? null);
        const payload = {
          title: editingPost.title,
          description: editingPost.content,
          fileMetadataDto: newFileMetadata ?? undefined,
        };

        const updatedPost = await updatePost(currentUsername, postId, payload);
        setPosts((prev) =>
          prev.map((post) => (post.id === postId ? updatedPost : post))
        );
        setCurrentMediaIndex((prev) => ({ ...prev, [postId]: 0 }));
        setEditingPost(null);
        toast.success(t("postUpdatedSuccess"));
        fetchPosts(activeTab === "my");
      } catch (error: any) {
        const errMsg = error?.response?.data?.message || error.message || t("failedUpdatePost");
        toast.error(errMsg);
      }
    },
    [editingPost, posts, currentUsername, t, activeTab, fetchPosts]
  );

  const handleAddComment = useCallback(
    async (e: React.FormEvent, postId: number) => {
      e.preventDefault();
      const commentText = newComment[postId]?.trim();
      if (!commentText) return;

      try {
        const commentDto: CommentRequestDto = { postId, comment: commentText };
        const newCommentData = await createComment(currentUsername, commentDto);
        setComments((prev) => ({
          ...prev,
          [postId]: [...(prev[postId] || []), newCommentData],
        }));
        setNewComment((prev) => ({ ...prev, [postId]: "" }));
        setPosts((prev) =>
          prev.map((post) =>
            post.id === postId
              ? { ...post, totalComments: post.totalComments + 1 }
              : post
          )
        );
        const updatedComments = await getCommentsByPost(currentUsername, postId);
        setComments((prev) => ({ ...prev, [postId]: updatedComments }));
      } catch (error: any) {
        const errMsg = error?.response?.data?.message || error.message || t("failedAddComment", "Failed to add comment");
        toast.error(errMsg);
      }
    },
    [currentUsername, newComment, t]
  );

  const handleDeleteComment = useCallback(
    async (postId: number, commentId: number) => {
      try {
        await deleteComment(currentUsername, commentId);
        setComments((prev) => ({
          ...prev,
          [postId]: prev[postId].filter((comment) => comment.id !== commentId),
        }));
        setPosts((prev) =>
          prev.map((post) =>
            post.id === postId
              ? { ...post, totalComments: post.totalComments - 1 }
              : post
          )
        );
        toast.success(t("commentDeletedSuccess"));
      } catch (error: any) {
        const errMsg = error?.response?.data?.message || error.message;
        toast.error(errMsg);
      }
    },
    [currentUsername, t]
  );

  const toggleComments = useCallback(
    async (postId: number) => {
      const isExpanded = expandedComments[postId];
      if (!isExpanded) {
        try {
          const postComments = await getCommentsByPost(currentUsername, postId);
          setComments((prev) => ({ ...prev, [postId]: postComments }));
        } catch (error) {
          toast.error(t("failedFetchComments", "Failed to load comments"));
        }
      }
      setExpandedComments((prev) => ({ ...prev, [postId]: !isExpanded }));
    },
    [currentUsername, expandedComments, t]
  );

  const handleOpenReportModal = useCallback((postId: number) => {
    setReportModalPostId(postId);
    setReportReason("");
    setShowOptionsMenu(null);
  }, []);

  const handleCloseReportModal = useCallback(() => {
    setReportModalPostId(null);
    setReportReason("");
  }, []);

  const handleConfirmReport = useCallback(
    async (postId: number) => {
      if (!reportReason.trim()) {
        toast.error(t("reportReasonRequired", "Please provide a reason for reporting"));
        return;
      }

      try {
        const payload: CreateAbuseReportRequest = {
          reportedTo: postId,
          reason: reportReason.trim(),
          type: 'POST',
        };

        console.log('Report Payload:', payload);
        
        const response = await createAbuseReport(payload);
        
        console.log('Report Response:', response);
        
        toast.success(t("reportSubmitted", "Report submitted successfully"));
        handleCloseReportModal();
      } catch (error: any) {
        console.error('Report Error:', error);
        const errMsg = error?.response?.data?.message || error.message;
        toast.error(errMsg);
      }
    },
    [reportReason, t, handleCloseReportModal]
  );

  const filterProps = useMemo(
    () => ({
      searchQuery,
      setSearchQuery,
      selectedSport,
      setSelectedSport,
      selectedWeight,
      setSelectedWeight,
      selectedManagerType,
      setSelectedManagerType,
      selectedProfileType,
      setSelectedProfileType,
      sports,
      weightClasses,
      loadSportsIfNeeded,
      loadWeightClassesIfNeeded,
      resetFilters,
      activeTab,
    }),
    [
      searchQuery,
      selectedSport,
      selectedWeight,
      selectedManagerType,
      selectedProfileType,
      sports,
      weightClasses,
      loadSportsIfNeeded,
      loadWeightClassesIfNeeded,
      resetFilters,
      activeTab,
    ]
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <FeedsFilter
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        t={t}
        filterProps={filterProps}
      />
      <FeedsContent
        loading={loading}
        posts={posts}
        topProfiles={topProfiles}
        activeTab={activeTab}
        currentUserId={currentUserId}
        likedPosts={likedPosts}
        comments={comments}
        newComment={newComment}
        expandedComments={expandedComments}
        currentMediaIndex={currentMediaIndex}
        editingPost={editingPost}
        showOptionsMenu={showOptionsMenu}
        showLikesModal={showLikesModal}
        loadingLikes={loadingLikes}
        likesData={likesData}
        deleteModalPostId={deleteModalPostId}
        reportModalPostId={reportModalPostId}
        reportReason={reportReason}
        t={t}
        onLike={handleLike}
        onShowLikes={handleShowLikes}
        onCloseLikesModal={handleCloseLikesModal}
        onEdit={handleEdit}
        onDelete={() => setDeleteModalPostId(posts[0]?.id || null)}
        onSavePost={handleSavePost}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteComment}
        onToggleComments={toggleComments}
        onSetNewComment={setNewComment}
        onSetShowOptionsMenu={setShowOptionsMenu}
        onSetEditingPost={setEditingPost}
        onRemoveSingleMedia={removeSingleMedia}
        onSetCurrentMediaIndex={setCurrentMediaIndex}
        onCloseDeleteModal={() => setDeleteModalPostId(null)}
        onConfirmDelete={handleDelete}
        onOpenReportModal={handleOpenReportModal}
        onCloseReportModal={handleCloseReportModal}
        onSetReportReason={setReportReason}
        onConfirmReport={handleConfirmReport}
      />
    </div>
  );
};

export default Feeds;
